import { FC } from "react";
import GridLayout, { type Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Skeleton } from "@capacit-com/lara-law-shared-ui";
import type { DashboardSkeletonLayoutWidget } from "@/lib/dashboard-skeleton-cache";
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_CONTAINER_PADDING,
  DASHBOARD_GRID_MARGIN,
  DASHBOARD_ROW_HEIGHT,
} from "../dashboard-grid-layout";
import { useDashboardGridContainerWidth } from "./useDashboardGridContainerWidth";

const StaticGridFallback: FC = () => (
  <div className="grid grid-cols-12 gap-4 h-full">
    <div className="col-span-6 row-span-2">
      <Skeleton className="h-[280px] rounded-lg" />
    </div>
    <div className="col-span-6 row-span-2">
      <Skeleton className="h-[280px] rounded-lg" />
    </div>
    <div className="col-span-4 row-span-1">
      <Skeleton className="h-[200px] rounded-lg" />
    </div>
    <div className="col-span-4 row-span-1">
      <Skeleton className="h-[200px] rounded-lg" />
    </div>
    <div className="col-span-4 row-span-1">
      <Skeleton className="h-[200px] rounded-lg" />
    </div>
  </div>
);

type DashboardSkeletonProps = {
  cachedWidgets?: DashboardSkeletonLayoutWidget[] | null;
};

function toGridLayout(widgets: DashboardSkeletonLayoutWidget[]): Layout {
  return widgets.map((w) => ({
    i: w.id,
    x: w.position.x,
    y: w.position.y,
    w: w.position.w,
    h: w.position.h,
    minW: 3,
    minH: 2,
  }));
}

const DashboardSkeleton: FC<DashboardSkeletonProps> = ({ cachedWidgets }) => {
  const { containerRef, containerWidth } = useDashboardGridContainerWidth();

  const cachedLayout: Layout | null =
    cachedWidgets && cachedWidgets.length > 0
      ? toGridLayout(cachedWidgets)
      : null;

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-200">
      <div className="flex items-center justify-between p-4 border-b gap-4">
        <div className="flex items-center gap-4 flex-1">
          <Skeleton className="h-7 w-64" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-[200px]" />
          <Skeleton className="h-9 w-[140px]" />
          <Skeleton className="h-9 w-[160px]" />
          <Skeleton className="h-9 w-[100px]" />
        </div>
      </div>

      <div className="themed-scrollbar scrollbar-gutter-stable flex-1 p-4 overflow-hidden">
        <div ref={containerRef} className="w-full h-full">
          {cachedLayout && containerWidth > 0 ? (
            <div className="animate-in fade-in duration-200">
              <GridLayout
                className="layout"
                layout={cachedLayout}
                width={containerWidth}
                gridConfig={{
                  cols: DASHBOARD_GRID_COLS,
                  rowHeight: DASHBOARD_ROW_HEIGHT,
                  margin: [...DASHBOARD_GRID_MARGIN],
                  containerPadding: [...DASHBOARD_GRID_CONTAINER_PADDING],
                }}
                resizeConfig={{ enabled: false }}
                dragConfig={{ enabled: false }}
              >
                {cachedLayout.map((item) => (
                  <div key={item.i}>
                    <Skeleton className="h-full min-h-[80px] w-full rounded-lg" />
                  </div>
                ))}
              </GridLayout>
            </div>
          ) : (
            // No cached grid layout yet, or width not measured — static placeholder
            <StaticGridFallback />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSkeleton;
